import { Component } from '@theme/component';
import { fetchConfig, preloadImage, onAnimationEnd, yieldToMainThread } from '@theme/utilities';
import { ThemeEvents, CartAddEvent, CartErrorEvent, CartUpdateEvent, VariantUpdateEvent } from '@theme/events';
import { cartPerformance } from '@theme/performance';
import { morph } from '@theme/morph';

// Error message display duration - gives users time to read the message
const ERROR_MESSAGE_DISPLAY_DURATION = 10000;

// Button re-enable delay after error - prevents rapid repeat attempts
const ERROR_BUTTON_REENABLE_DELAY = 1000;

// Success message display duration for screen readers
const SUCCESS_MESSAGE_DISPLAY_DURATION = 5000;

// What the product page's buttons say after an add, in place of their label (the CSS is in
// sections/product-information.liquid). Short and all caps like the button labels.
const FEEDBACK = {
  added: 'ADDED ✓',
  lastAdded: 'LAST ONE ADDED ✓',
  inBag: 'LAST ONE’S IN YOUR BAG',
  soldOut: 'SOLD OUT',
  onlyLeft: (/** @type {number} */ count) => `ONLY ${count} LEFT`,
};
const FEEDBACK_ADDED_MS = 1500;
const FEEDBACK_REFUSED_MS = 1800;

/**
 * Whether an add from here shows the messages above: the product page's own form, not collection
 * quick-add or its modal.
 * @param {Element} element
 */
function showsAddFeedback(element) {
  return Boolean(element.closest('.product-information')) && !element.closest('.quick-add-modal, quick-add-dialog');
}

/** @type {WeakMap<HTMLElement, number>} */
const flashTimers = new WeakMap();

/**
 * Shows a message in place of a button's label for a moment. The label itself is left alone
 * (data-flash + CSS), so a variant change or the PACK row relabelling the button meanwhile is fine.
 * @param {HTMLElement | null | undefined} button
 * @param {string} text
 * @param {number} ms
 * @param {boolean} pulse - The little shrink/grow.
 */
function flashButton(button, text, ms, pulse) {
  if (!button) return;
  clearTimeout(flashTimers.get(button));
  button.dataset.flash = text;
  button.classList.remove('is-pulse');
  if (pulse) {
    void button.offsetWidth; // restart the pulse when a second add lands during the first message
    button.classList.add('is-pulse');
  }
  flashTimers.set(
    button,
    window.setTimeout(() => {
      button.removeAttribute('data-flash');
      button.classList.remove('is-pulse');
    }, ms)
  );
}

/**
 * The stock caps and bag counts rendered with the form ([data-stock-limits] in
 * blocks/buy-buttons.liquid), or null.
 * @param {ParentNode} root
 * @returns {StockLimits | null}
 */
function readStockLimits(root) {
  try {
    return JSON.parse(root.querySelector('[data-stock-limits]')?.textContent || '');
  } catch (error) {
    return null;
  }
}

/**
 * Units in the bag per variant id.
 * @param {{items: Array<{quantity: number, variant_id: number}>}} cart
 * @returns {Record<string, number>}
 */
function cartCounts(cart) {
  /** @type {Record<string, number>} */
  const counts = {};
  for (const item of cart.items) counts[item.variant_id] = (counts[item.variant_id] || 0) + item.quantity;
  return counts;
}

/**
 * @typedef {HTMLElement & {
 *   source: Element,
 *   destination: Element,
 *   useSourceSize: string | boolean
 * }} FlyToCart
 */

/**
 * A custom element that manages an add to cart button.
 *
 * @typedef {object} AddToCartRefs
 * @property {HTMLButtonElement} addToCartButton - The add to cart button.
 * @extends Component<AddToCartRefs>
 */
export class AddToCartComponent extends Component {
  requiredRefs = ['addToCartButton'];

  /** @type {number[] | undefined} */
  #resetTimeouts = /** @type {number[]} */ ([]);

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('pointerenter', this.#preloadImage);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    if (this.#resetTimeouts) {
      this.#resetTimeouts.forEach(/** @param {number} timeoutId */ (timeoutId) => clearTimeout(timeoutId));
    }
    this.removeEventListener('pointerenter', this.#preloadImage);
  }

  /**
   * Disables the add to cart button.
   */
  disable() {
    this.refs.addToCartButton.disabled = true;
  }

  /**
   * Enables the add to cart button.
   */
  enable() {
    this.refs.addToCartButton.disabled = false;
  }

  /**
   * Handles the click event for the add to cart button.
   * @param {MouseEvent & {target: HTMLElement}} event - The click event.
   */
  handleClick(event) {
    const form = this.closest('form');
    if (!form?.checkValidity()) return;

    // Check if adding would exceed max before animating
    const productForm = /** @type {ProductFormComponent | null} */ (this.closest('product-form-component'));
    const quantitySelector = productForm?.refs.quantitySelector;
    if (quantitySelector?.canAddToCart) {
      const validation = quantitySelector.canAddToCart();
      // Don't animate if it would exceed max
      if (!validation.canAdd) {
        return;
      }
    }
    if (this.refs.addToCartButton.dataset.puppet !== 'true') {
      const animationEnabled = this.dataset.addToCartAnimation === 'true';
      if (animationEnabled && !event.target.closest('.quick-add-modal')) {
        this.#animateFlyToCart();
      }
      // The product page's buttons say what happened once the add is through instead
      // (ProductFormComponent#feedback).
      if (!showsAddFeedback(this)) this.animateAddToCart();
    }
  }

  #preloadImage = () => {
    const image = this.dataset.productVariantMedia;

    if (!image) return;

    preloadImage(image);
  };

  /**
   * Animates the fly to cart animation.
   */
  #animateFlyToCart() {
    const { addToCartButton } = this.refs;
    const cartIcon = document.querySelector('.header-actions__cart-icon');

    const image = this.dataset.productVariantMedia;

    if (!cartIcon || !addToCartButton || !image) return;

    const flyToCartElement = /** @type {FlyToCart} */ (document.createElement('fly-to-cart'));

    let flyToCartClass = addToCartButton.classList.contains('quick-add__button')
      ? 'fly-to-cart--quick'
      : 'fly-to-cart--main';

    flyToCartElement.classList.add(flyToCartClass);
    flyToCartElement.style.setProperty('background-image', `url(${image})`);
    flyToCartElement.style.setProperty('--start-opacity', '0');
    flyToCartElement.source = addToCartButton;
    flyToCartElement.destination = cartIcon;

    document.body.appendChild(flyToCartElement);
  }

  /**
   * Animates the add to cart button.
   */
  animateAddToCart = async function () {
    const { addToCartButton } = this.refs;

    // Initialize the array if it doesn't exist
    if (!this.#resetTimeouts) {
      this.#resetTimeouts = [];
    }

    // Clear all existing timeouts
    this.#resetTimeouts.forEach(/** @param {number} timeoutId */ (timeoutId) => clearTimeout(timeoutId));
    this.#resetTimeouts = [];

    if (addToCartButton.dataset.added !== 'true') {
      addToCartButton.dataset.added = 'true';
    }

    // The onAnimationEnd can trigger a style recalculation so we yield to the main thread first.
    await yieldToMainThread();
    await onAnimationEnd(addToCartButton);

    // Create new timeout and store it in the array
    const timeoutId = setTimeout(() => {
      addToCartButton.removeAttribute('data-added');

      // Remove this timeout from the array
      const index = this.#resetTimeouts.indexOf(timeoutId);
      if (index > -1) {
        this.#resetTimeouts.splice(index, 1); 
      }
    }, 800);

    this.#resetTimeouts.push(timeoutId);
  };
}

if (!customElements.get('add-to-cart-component')) {
  customElements.define('add-to-cart-component', AddToCartComponent);
}

/**
 * A custom element that manages a product form.
 *
 * @typedef {{items: Array<{quantity: number, variant_id: number}>}} Cart
 * @typedef {{variantId: string, quantity: number}} CartLine
 * @typedef {{stock: Record<string, number | null>, cart: Record<string, number>}} StockLimits
 *
 * @typedef {object} ProductFormRefs
 * @property {HTMLInputElement} variantId - The form input for submitting the variant ID.
 * @property {AddToCartComponent | undefined} addToCartButtonContainer - The add to cart button container element.
 * @property {HTMLElement | undefined} addToCartTextError - The add to cart text error.
 * @property {HTMLElement | undefined} acceleratedCheckoutButtonContainer - The accelerated checkout button container element.
 * @property {HTMLElement} liveRegion - The live region.
 * @property {HTMLElement | undefined} quantityLabelCartCount - The quantity label cart count element.
 * @property {HTMLElement | undefined} quantityRules - The quantity rules element.
 * @property {HTMLElement | undefined} productFormButtons - The product form buttons container.
 * @property {HTMLElement | undefined} volumePricing - The volume pricing component.
 * @property {any | undefined} quantitySelector - The quantity selector component.
 * @property {HTMLElement | undefined} quantitySelectorWrapper - The quantity selector wrapper element.
 * @property {HTMLElement | undefined} quantityLabel - The quantity label element.
 * @property {HTMLElement | undefined} pricePerItem - The price per item component.
 *
 * @extends Component<ProductFormRefs>
 */
class ProductFormComponent extends Component {
  requiredRefs = ['variantId', 'liveRegion'];
  #abortController = new AbortController();

  /** @type {number | undefined} */
  #timeout;

  /** @type {boolean} */
  #variantChangeInProgress = false;

  /** @type {Array<{variantId: string, quantity: number, properties?: Record<string, string>}>} */
  #addToCartQueue = [];

  /**
   * How many of each variant Shopify will sell (null: no cap) and how many are in the bag, for the
   * "last one" messages. The bag counts follow every cart change from here on.
   * @type {StockLimits}
   */
  #limits = { stock: {}, cart: {} };

  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#abortController;
    const target = this.closest('.shopify-section, dialog, product-card');
    target?.addEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate, { signal });
    target?.addEventListener(ThemeEvents.variantSelected, this.#onVariantSelected, { signal });

    // Listen for cart updates to sync data-cart-quantity
    document.addEventListener(ThemeEvents.cartUpdate, this.#onCartUpdate, { signal });

    this.#limits = readStockLimits(this) || this.#limits;
    // Back/forward cache: the bag may have changed on another page since this one rendered.
    window.addEventListener(
      'pageshow',
      (event) => {
        if (event.persisted) this.#fetchAndUpdateCartQuantity();
      },
      { signal }
    );
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#abortController.abort();
  }

  /**
   * Updates quantity selector with cart data for current variant
   * @param {Cart} cart - The cart object with items array
   * @returns {number} The cart quantity for the current variant
   */
  #updateCartQuantityFromData(cart) {
    if (cart?.items) this.#limits.cart = cartCounts(cart);

    const variantIdInput = /** @type {HTMLInputElement | null} */ (this.querySelector('input[name="id"]'));
    if (!variantIdInput?.value || !cart?.items) return 0;

    const cartItem = cart.items.find((item) => item.variant_id.toString() === variantIdInput.value.toString());
    const cartQty = cartItem ? cartItem.quantity : 0;

    // Use public API to update quantity selector
    const quantitySelector = /** @type {any | undefined} */ (this.querySelector('quantity-selector-component'));
    if (quantitySelector?.setCartQuantity) {
      quantitySelector.setCartQuantity(cartQty);
    }

    // Update quantity label if it exists
    this.#updateQuantityLabel(cartQty);

    return cartQty;
  }

  /**
   * Fetches cart and updates quantity selector for current variant
   * @returns {Promise<number>} The cart quantity for the current variant
   */
  async #fetchAndUpdateCartQuantity() {
    const variantIdInput = /** @type {HTMLInputElement | null} */ (this.querySelector('input[name="id"]'));
    if (!variantIdInput?.value) return 0;

    try {
      const response = await fetch('/cart.js');
      const cart = await response.json();

      return this.#updateCartQuantityFromData(cart);
    } catch (error) {
      console.error('Failed to fetch cart quantity:', error);
      return 0;
    }
  }

  /**
   * Updates data-cart-quantity when cart is updated from elsewhere
   * @param {CartUpdateEvent|CartAddEvent} event
   */
  #onCartUpdate = async (event) => {
    // Skip if this event came from this component
    if (event.detail?.sourceId === this.id || event.detail?.data?.source === 'product-form-component') return;

    const cart = /** @type {Cart} */ (event.detail?.resource);
    if (cart?.items) {
      this.#updateCartQuantityFromData(cart);
    } else {
      await this.#fetchAndUpdateCartQuantity();
    }
  };

  /** @param {Event} event */
  handleSubmit(event) {
    event.preventDefault();

    if (this.#variantChangeInProgress) {
      const intendedVariantId = this.#getIntendedVariantId();
      const quantity = this.#getQuantity();
      const packItems = this.#getPackItems(intendedVariantId);
      const items = packItems ?? (intendedVariantId ? [{ variantId: intendedVariantId, quantity }] : []);

      if (this.#refuse(items)) return;
      this.#addToCartQueue.push(...items);

      if (!showsAddFeedback(this)) this.refs.addToCartButtonContainer?.animateAddToCart?.();
      return;
    }

    // Pack 2 or 3 (snippets/pack-offer.liquid): every tee in one /cart/add.js call.
    const packItems = this.#getPackItems(this.refs.variantId.value);
    if (packItems) {
      if (packItems.length === 0 || this.refs.addToCartButtonContainer?.refs.addToCartButton?.disabled) return;
      if (this.#refuse(packItems)) return;
      this.#processBatchAddToCart(packItems);
      return;
    }

    if (this.#refuse([{ variantId: this.refs.variantId.value, quantity: this.#getQuantity() }])) return;
    this.#processAddToCart(undefined, undefined, event);
  }

  /**
   * How many of this variant Shopify will sell, or null when it isn't capped: not tracked,
   * "Continue selling when out of stock" on, or a pre-order size.
   * @param {string} variantId
   * @returns {number | null}
   */
  #cap(variantId) {
    const cap = this.#limits.stock[variantId];
    if (cap == null || this.#preorderStates()[variantId] === true) return null;
    return cap;
  }

  /**
   * Stops an add the stock can't cover before it is sent, and says why on the button. Shopify would
   * refuse it with "…in your cart" text next to the button; this keeps it on the button.
   * @param {CartLine[]} items
   * @returns {boolean} true when the add must not be sent
   */
  #refuse(items) {
    if (!showsAddFeedback(this)) return false;

    /** @type {string | null} */
    let message = null;
    for (const { variantId, quantity } of items) {
      const cap = this.#cap(variantId);
      if (cap == null) continue;

      const inBag = this.#limits.cart[variantId] || 0;
      const room = cap - inBag;
      if (room <= 0) {
        message = inBag > 0 ? FEEDBACK.inBag : FEEDBACK.soldOut;
        break;
      }
      // Only a PACK asks for more than one: don't half-add it.
      if (quantity > room) message = FEEDBACK.onlyLeft(room);
    }

    if (!message) return false;
    this.#feedback(message, FEEDBACK_REFUSED_MS, false);
    return true;
  }

  /**
   * After a successful add: "LAST ONE ADDED ✓" when the bag now holds all that's left of one of
   * these variants (on purpose, for urgency), "ADDED ✓" otherwise. Bag counts are fresh by now.
   * @param {CartLine[]} items
   */
  #feedbackAdded(items) {
    if (!showsAddFeedback(this)) return;

    const last = items.some(({ variantId }) => {
      const cap = this.#cap(variantId);
      return cap != null && (this.#limits.cart[variantId] || 0) >= cap;
    });
    this.#feedback(last ? FEEDBACK.lastAdded : FEEDBACK.added, FEEDBACK_ADDED_MS, true);
  }

  /**
   * Shopify refused an add that had a capped variant in it, so the page's stock or bag counts
   * were out of date. Reads the bag again and answers on the button, never in the error text.
   * @param {CartLine[]} items
   * @returns {Promise<boolean>} true when handled here
   */
  async #handleStockError(items) {
    if (!showsAddFeedback(this)) return false;
    const capped = items.filter(({ variantId }) => this.#cap(variantId) != null);
    if (capped.length === 0) return false;

    const before = { ...this.#limits.cart };
    await this.#fetchAndUpdateCartQuantity();
    const { cart } = this.#limits;

    // Shopify adds what it can of a single line before refusing the rest.
    const added = items.reduce(
      (sum, { variantId }) => sum + Math.max(0, (cart[variantId] || 0) - (before[variantId] || 0)),
      0
    );

    // With one capped line it's clear which variant ran out: what's in the bag is all there is.
    // With more it isn't, so the caps are left as they are rather than guessed.
    const only = capped.length === 1 ? capped[0] : null;
    if (only) this.#limits.stock[only.variantId] = cart[only.variantId] || 0;

    if (added > 0) {
      this.dispatchEvent(
        new CartAddEvent({}, this.id, {
          source: 'product-form-component',
          itemCount: added,
          productId: this.dataset.productId,
        })
      );
      this.#feedback(FEEDBACK.lastAdded, FEEDBACK_ADDED_MS, true);
      return true;
    }

    const inBag = capped.some(({ variantId }) => (cart[variantId] || 0) > 0);
    this.#feedback(inBag ? FEEDBACK.inBag : FEEDBACK.soldOut, FEEDBACK_REFUSED_MS, false);
    return true;
  }

  /**
   * Shows a message on this form's button, on the sticky bar's (same product) and, through
   * add-to-cart:feedback, on the phone button (all in sections/product-information.liquid).
   * @param {string} text
   * @param {number} ms
   * @param {boolean} pulse - The little shrink/grow, for adds that went through.
   */
  #feedback(text, ms, pulse) {
    const { productId } = this.dataset;

    flashButton(this.refs.addToCartButtonContainer?.refs.addToCartButton, text, ms, pulse);
    flashButton(
      document.querySelector(`sticky-add-to-cart[data-product-id="${productId}"] [ref="addToCartButton"]`),
      text,
      ms,
      pulse
    );
    document.dispatchEvent(new CustomEvent('add-to-cart:feedback', { detail: { productId, text, ms, pulse } }));

    this.#setLiveRegionText(text.replace(' ✓', ''));
    setTimeout(() => this.#clearLiveRegionText(), SUCCESS_MESSAGE_DISPLAY_DURATION);
  }

  /**
   * Variant id -> whether it is a pre-order size ([data-preorder-variants] in blocks/buy-buttons.liquid).
   * @returns {Record<string, boolean>}
   */
  #preorderStates() {
    try {
      return JSON.parse(this.querySelector('[data-preorder-variants]')?.textContent || '{}');
    } catch (error) {
      return {};
    }
  }

  /**
   * The selected pack's items from the PACK row, each with the line item properties a normal add
   * would give that variant, or null for a normal single add.
   * @param {string | undefined} tee1Id
   * @returns {Array<{variantId: string, quantity: number, properties: Record<string, string>}> | null}
   */
  #getPackItems(tee1Id) {
    const packPicker = /** @type {any} */ (this.closest('.buy-buttons-block')?.querySelector('pack-picker'));
    /** @type {Array<{variantId: string, quantity: number}> | null} */
    const items = tee1Id && packPicker?.getItems ? packPicker.getItems(tee1Id) : null;
    return items && items.map((item) => ({ ...item, properties: this.#lineItemProperties(item.variantId) }));
  }

  /**
   * The properties a normal add sends for this variant: the form's own properties[...] inputs,
   * except Preorder, which follows the variant. It is set from the same map that toggles the
   * properties[Preorder] input for the main picker (blocks/buy-buttons.liquid).
   * @param {string} variantId
   * @returns {Record<string, string>}
   */
  #lineItemProperties(variantId) {
    const propertyKey = (/** @type {string} */ name) => name.match(/^properties\[(.+)\]$/)?.[1];
    const preorderInput = /** @type {HTMLInputElement | null} */ (this.querySelector('[data-preorder-property]'));
    const form = this.querySelector('form');

    /** @type {Record<string, string>} */
    const properties = {};
    for (const [name, value] of form ? new FormData(form) : []) {
      const key = propertyKey(name);
      if (key && name !== preorderInput?.name && typeof value === 'string') properties[key] = value;
    }

    const preorderKey = preorderInput && propertyKey(preorderInput.name);
    if (preorderKey && this.#preorderStates()[variantId] === true) properties[preorderKey] = preorderInput.value;

    return properties;
  }

  /** @returns {string | undefined} */
  #getIntendedVariantId() {
    return new URL(window.location.href).searchParams.get('variant') || this.refs.variantId?.value || undefined;
  }

  /** @returns {number} */
  #getQuantity() {
    return Number(this.refs.quantitySelector?.getValue?.()) || Number(this.dataset.quantityDefault) || 1;
  }

  /**
   * @param {string} [overrideVariantId]
   * @param {number} [overrideQuantity]
   * @param {Event} [event]
   */
  #processAddToCart(overrideVariantId, overrideQuantity, event) {
    const { addToCartTextError } = this.refs;

    if (this.#timeout) clearTimeout(this.#timeout);

    const allAddToCartContainers = /** @type {NodeListOf<AddToCartComponent>} */ (
      this.querySelectorAll('add-to-cart-component')
    );

    if (!overrideVariantId) {
      const anyButtonDisabled = Array.from(allAddToCartContainers).some(
        (container) => container.refs.addToCartButton?.disabled
      );
      if (anyButtonDisabled) return;
    }

    const form = this.querySelector('form');
    if (!form) throw new Error('Product form element missing');

    if (!overrideVariantId && this.refs.quantitySelector?.canAddToCart) {
      const validation = this.refs.quantitySelector.canAddToCart();

      if (!validation.canAdd) {
        for (const container of allAddToCartContainers) {
          container.disable();
        }

        const errorTemplate = this.dataset.quantityErrorMax || '';
        const errorMessage = errorTemplate.replace('{{ maximum }}', validation.maxQuantity?.toString() || '');
        if (addToCartTextError) {
          addToCartTextError.classList.remove('hidden');

          const textNode = addToCartTextError.childNodes[2];
          if (textNode) {
            textNode.textContent = errorMessage;
          } else {
            const newTextNode = document.createTextNode(errorMessage);
            addToCartTextError.appendChild(newTextNode);
          }

          this.#setLiveRegionText(errorMessage);

          if (this.#timeout) clearTimeout(this.#timeout);
          this.#timeout = setTimeout(() => {
            if (!addToCartTextError) return;
            addToCartTextError.classList.add('hidden');
            this.#clearLiveRegionText();
          }, ERROR_MESSAGE_DISPLAY_DURATION);
        }

        setTimeout(() => {
          for (const container of allAddToCartContainers) {
            container.enable();
          }
        }, ERROR_BUTTON_REENABLE_DELAY);

        return;
      }
    }

    const formData = new FormData(form);

    if (overrideVariantId) {
      formData.set('id', overrideVariantId);
    }
    if (overrideQuantity !== undefined) {
      formData.set('quantity', overrideQuantity.toString());
    }

    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    let cartItemComponentsSectionIds = [];
    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        cartItemComponentsSectionIds.push(item.dataset.sectionId);
      }
      formData.append('sections', cartItemComponentsSectionIds.join(','));
    });

    const fetchCfg = fetchConfig('javascript', { body: formData });

    fetch(Theme.routes.cart_add_url, {
      ...fetchCfg,
      headers: {
        ...fetchCfg.headers,
        Accept: 'text/html',
      },
    })
      .then((response) => response.json())
      .then(async (response) => {
        if (response.status) {
          const line = {
            variantId: String(formData.get('id')),
            quantity: Number(formData.get('quantity')) || Number(this.dataset.quantityDefault) || 1,
          };
          if (await this.#handleStockError([line])) return;

          this.dispatchEvent(
            new CartErrorEvent(form.getAttribute('id') || '', response.message, response.description, response.errors)
          );

          if (!addToCartTextError) return;
          addToCartTextError.classList.remove('hidden');

          // Reuse the text node if the user is spam-clicking
          const textNode = addToCartTextError.childNodes[2];
          if (textNode) {
            textNode.textContent = response.message;
          } else {
            const newTextNode = document.createTextNode(response.message);
            addToCartTextError.appendChild(newTextNode);
          }

          // Create or get existing error live region for screen readers
          this.#setLiveRegionText(response.message);

          this.#timeout = setTimeout(() => {
            if (!addToCartTextError) return;
            addToCartTextError.classList.add('hidden');

            // Clear the announcement
            this.#clearLiveRegionText();
          }, ERROR_MESSAGE_DISPLAY_DURATION);

          // When we add more than the maximum amount of items to the cart, we need to dispatch a cart update event
          // because our back-end still adds the max allowed amount to the cart.
          this.dispatchEvent(
            new CartAddEvent({}, this.id, {
              didError: true,
              source: 'product-form-component',
              itemCount: Number(formData.get('quantity')) || Number(this.dataset.quantityDefault),
              productId: this.dataset.productId,
            })
          );

          return;
        } else {
          const id = formData.get('id');

          if (addToCartTextError) {
            addToCartTextError.classList.add('hidden');
            addToCartTextError.removeAttribute('aria-live');
          }

          if (!id) throw new Error('Form ID is required');

          // Add aria-live region to inform screen readers that the item was added
          // Get the added text from any add-to-cart button
          const anyAddToCartButton = allAddToCartContainers[0]?.refs.addToCartButton;
          if (anyAddToCartButton) {
            const addedTextElement = anyAddToCartButton.querySelector('.add-to-cart-text--added');
            const addedText = addedTextElement?.textContent?.trim() || Theme.translations.added;

            this.#setLiveRegionText(addedText);

            setTimeout(() => {
              this.#clearLiveRegionText();
            }, SUCCESS_MESSAGE_DISPLAY_DURATION);
          }

          // Fetch the updated cart to get the actual total quantity for this variant
          await this.#fetchAndUpdateCartQuantity();

          this.dispatchEvent(
            new CartAddEvent({}, id.toString(), {
              source: 'product-form-component',
              itemCount: Number(formData.get('quantity')) || Number(this.dataset.quantityDefault),
              productId: this.dataset.productId,
              sections: response.sections,
            })
          );

          this.#feedbackAdded([{ variantId: id.toString(), quantity: Number(formData.get('quantity')) || 1 }]);
        }
      })
      .catch((error) => {
        console.error(error);
      })
      .finally(() => {
        if (event) {
          cartPerformance.measureFromEvent('add:user-action', event);
        }
      });
  }

  /** @param {Array<{variantId: string, quantity: number, properties?: Record<string, string>}>} items */
  #processBatchAddToCart(items) {
    if (items.length === 0) return;

    const { addToCartTextError } = this.refs;

    if (this.#timeout) clearTimeout(this.#timeout);

    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    const cartItemComponentsSectionIds = [];
    for (const item of cartItemsComponents) {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        cartItemComponentsSectionIds.push(item.dataset.sectionId);
      }
    }

    const payload = {
      items: items.map((item) => ({
        id: Number(item.variantId),
        quantity: item.quantity,
        // Only when there are some, so a line without properties is sent exactly as before.
        ...(item.properties && Object.keys(item.properties).length > 0 && { properties: item.properties }),
      })),
      sections: cartItemComponentsSectionIds.join(','),
    };

    fetch(Theme.routes.cart_add_url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify(payload),
    })
      .then((response) => response.json())
      .then(async (response) => {
        if (response.status) {
          if (await this.#handleStockError(items)) return;

          this.dispatchEvent(
            new CartErrorEvent(this.id, response.message, response.description, response.errors)
          );

          if (addToCartTextError) {
            addToCartTextError.classList.remove('hidden');
            const textNode = addToCartTextError.childNodes[2];
            if (textNode) {
              textNode.textContent = response.message;
            } else {
              addToCartTextError.appendChild(document.createTextNode(response.message));
            }
            this.#setLiveRegionText(response.message);

            this.#timeout = setTimeout(() => {
              addToCartTextError.classList.add('hidden');
              this.#clearLiveRegionText();
            }, ERROR_MESSAGE_DISPLAY_DURATION);
          }

          this.dispatchEvent(
            new CartAddEvent({}, this.id, {
              didError: true,
              source: 'product-form-component',
              itemCount: items.reduce((sum, item) => sum + item.quantity, 0),
              productId: this.dataset.productId,
            })
          );
          return;
        }

        if (addToCartTextError) {
          addToCartTextError.classList.add('hidden');
          addToCartTextError.removeAttribute('aria-live');
        }

        const allAddToCartContainers = /** @type {NodeListOf<AddToCartComponent>} */ (
          this.querySelectorAll('add-to-cart-component')
        );
        const anyAddToCartButton = allAddToCartContainers[0]?.refs.addToCartButton;
        if (anyAddToCartButton) {
          const addedTextElement = anyAddToCartButton.querySelector('.add-to-cart-text--added');
          const addedText = addedTextElement?.textContent?.trim() || Theme.translations.added;
          this.#setLiveRegionText(addedText);
          setTimeout(() => this.#clearLiveRegionText(), SUCCESS_MESSAGE_DISPLAY_DURATION);
        }

        await this.#fetchAndUpdateCartQuantity();

        const totalQuantity = items.reduce((sum, item) => sum + item.quantity, 0);
        this.dispatchEvent(
          new CartAddEvent({}, this.id, {
            source: 'product-form-component',
            itemCount: totalQuantity,
            productId: this.dataset.productId,
            sections: response.sections,
          })
        );

        this.#feedbackAdded(items);
      })
      .catch((error) => {
        console.error(error);
      });
  }

  /**
   * Updates the quantity label with the current cart quantity
   * @param {number} cartQty - The quantity in cart
   */
  #updateQuantityLabel(cartQty) {
    const quantityLabel = this.refs.quantityLabelCartCount;
    if (quantityLabel) {
      const inCartText = quantityLabel.textContent?.match(/\((\d+)\s+(.+)\)/);
      if (inCartText && inCartText[2]) {
        quantityLabel.textContent = `(${cartQty} ${inCartText[2]})`;
      }

      // Show/hide based on quantity
      quantityLabel.classList.toggle('hidden', cartQty === 0);
    }
  }

  /**
   * @param {*} text
   */
  #setLiveRegionText(text) {
    const liveRegion = this.refs.liveRegion;
    liveRegion.textContent = text;
  }

  #clearLiveRegionText() {
    const liveRegion = this.refs.liveRegion;
    liveRegion.textContent = '';
  }

  /**
   * Morphs or removes/adds an element based on current and new element states
   * @param {Element | null | undefined} currentElement - The current element in the DOM
   * @param {Element | null | undefined} newElement - The new element from the server response
   * @param {Element | null} [insertReferenceElement] - Element to insert before if adding new element
   */
  #morphOrUpdateElement(currentElement, newElement, insertReferenceElement = null) {
    if (currentElement && newElement) {
      morph(currentElement, newElement);
    } else if (currentElement && !newElement) {
      currentElement.remove();
    } else if (!currentElement && newElement && insertReferenceElement) {
      insertReferenceElement.insertAdjacentElement('beforebegin', /** @type {Element} */ (newElement.cloneNode(true)));
    }
  }

  /**
   * @param {VariantUpdateEvent} event
   */
  #onVariantUpdate = async (event) => {
    if (event.detail.data.newProduct) {
      this.dataset.productId = event.detail.data.newProduct.id;
    } else if (event.detail.data.productId !== this.dataset.productId) {
      return;
    }

    const { variantId } = this.refs;
    variantId.value = event.detail.resource?.id ?? '';

    // Fresh stock numbers came with the new variant's HTML.
    const freshForm = event.detail.data.html.querySelector(
      `product-form-component[data-section-id="${this.dataset.sectionId}"]`
    );
    const freshLimits = freshForm && readStockLimits(freshForm);
    if (freshLimits?.stock) this.#limits.stock = freshLimits.stock;

    this.#variantChangeInProgress = false;

    if (this.#addToCartQueue.length > 0) {
      const queuedItems = [...this.#addToCartQueue];
      this.#addToCartQueue = [];
      this.#processBatchAddToCart(queuedItems);
    }

    const { addToCartButtonContainer: currentAddToCartButtonContainer, acceleratedCheckoutButtonContainer } = this.refs;
    const currentAddToCartButton = currentAddToCartButtonContainer?.refs.addToCartButton;

    // Update state and text for add-to-cart button
    if (!currentAddToCartButtonContainer || (!currentAddToCartButton && !acceleratedCheckoutButtonContainer)) return;

    // Update the button state
    if (event.detail.resource == null || event.detail.resource.available == false) {
      currentAddToCartButtonContainer.disable();
    } else {
      currentAddToCartButtonContainer.enable();
    }

    const newAddToCartButton = event.detail.data.html.querySelector('product-form-component [ref="addToCartButton"]');
    if (newAddToCartButton && currentAddToCartButton) {
      morph(currentAddToCartButton, newAddToCartButton);
    }

    if (acceleratedCheckoutButtonContainer) {
      if (event.detail.resource == null || event.detail.resource.available == false) {
        acceleratedCheckoutButtonContainer?.setAttribute('hidden', 'true');
      } else {
        acceleratedCheckoutButtonContainer?.removeAttribute('hidden');
      }
    }

    // Set the data attribute for the product variant media if it exists
    if (event.detail.resource) {
      const productVariantMedia = event.detail.resource.featured_media?.preview_image?.src;
      if (productVariantMedia) {
        this.refs.addToCartButtonContainer?.setAttribute(
          'data-product-variant-media',
          productVariantMedia + '&width=100'
        );
      }
    }

    // Check if quantity rules, price-per-item, or add-to-cart are appearing/disappearing (causes layout shift)
    const {
      quantityRules,
      pricePerItem,
      quantitySelector,
      productFormButtons,
      quantityLabel,
      quantitySelectorWrapper,
    } = this.refs;

    // Update quantity selector's min/max/step attributes and cart quantity for the new variant
    const newQuantityInput = /** @type {HTMLInputElement | null} */ (
      event.detail.data.html.querySelector('quantity-selector-component input[ref="quantityInput"]')
    );

    if (quantitySelector?.updateConstraints && newQuantityInput) {
      quantitySelector.updateConstraints(newQuantityInput.min, newQuantityInput.max || null, newQuantityInput.step);
    }

    const newQuantityRules = event.detail.data.html.querySelector('.quantity-rules');
    const isQuantityRulesChanging = !!quantityRules !== !!newQuantityRules;

    const newPricePerItem = event.detail.data.html.querySelector('price-per-item');
    const isPricePerItemChanging = !!pricePerItem !== !!newPricePerItem;

    if ((isQuantityRulesChanging || isPricePerItemChanging) && quantitySelector) {
      // Store quantity value before morphing entire container
      const currentQuantityValue = quantitySelector.getValue?.();

      const newProductFormButtons = event.detail.data.html.querySelector('.product-form-buttons');

      if (productFormButtons && newProductFormButtons) {
        morph(productFormButtons, newProductFormButtons);

        // Get the NEW quantity selector after morphing and update its constraints
        const newQuantityInputElement = /** @type {HTMLInputElement | null} */ (
          event.detail.data.html.querySelector('quantity-selector-component input[ref="quantityInput"]')
        );

        if (this.refs.quantitySelector?.updateConstraints && newQuantityInputElement && currentQuantityValue) {
          // Temporarily set the old value so updateConstraints can snap it properly
          this.refs.quantitySelector.setValue(currentQuantityValue);
          // updateConstraints will snap to valid increment if needed
          this.refs.quantitySelector.updateConstraints(
            newQuantityInputElement.min,
            newQuantityInputElement.max || null,
            newQuantityInputElement.step
          );
        }
      }
    } else {
      // Update elements individually when layout isn't changing
      /** @type {Array<[string, HTMLElement | undefined, HTMLElement | undefined]>} */
      const morphTargets = [
        ['.quantity-label', quantityLabel, quantitySelector],
        ['.quantity-rules', quantityRules, this.refs.productFormButtons],
        ['price-per-item', pricePerItem, quantitySelectorWrapper],
      ];

      for (const [selector, currentElement, fallback] of morphTargets) {
        this.#morphOrUpdateElement(currentElement, event.detail.data.html.querySelector(selector), fallback);
      }
    }

    // Morph volume pricing if it exists
    const currentVolumePricing = this.refs.volumePricing;
    const newVolumePricing = event.detail.data.html.querySelector('volume-pricing');
    this.#morphOrUpdateElement(currentVolumePricing, newVolumePricing, this.refs.productFormButtons);

    const hasB2BFeatures =
      quantityRules || newQuantityRules || pricePerItem || newPricePerItem || currentVolumePricing || newVolumePricing;

    if (!hasB2BFeatures) return;

    // Fetch and update cart quantity for the new variant
    await this.#fetchAndUpdateCartQuantity();
  };

  /** @param {import('./events').VariantSelectedEvent} _event */
  #onVariantSelected = (_event) => {
    this.#variantChangeInProgress = true;
  };
}

if (!customElements.get('product-form-component')) {
  customElements.define('product-form-component', ProductFormComponent);
}
